import React, { useState, useEffect, useRef } from "react";
import Cubes3D from "../components/Cubes3D";

// Intervalle de rafraîchissement entropie dynamique
const DYN_ENTROPY_INTERVAL = 150;

export default function CubesPage() {
  // États entropie
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);
  const [loadingDyn, setLoadingDyn] = useState(true);

  // Contrôles UI
  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  const [cubeSpeedPct, setCubeSpeedPct] = useState(50);
  const [ballSpeedPct, setBallSpeedPct] = useState(50);
  const [pulseSpeedPct, setPulseSpeedPct] = useState(50);
  const [flashRatePct, setFlashRatePct] = useState(30);

  // Ref canvas pour snapshot
  const canvasRef = useRef(null);

  // --- Entropie UI (curseurs → backend /entropy)
  const debounceTimeout = useRef(null);
  useEffect(() => {
    setLoadingUI(true);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    const controller = new AbortController();
    debounceTimeout.current = setTimeout(() => {
      fetch("/api/geometry/cubes/entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cube_speed: cubeSpeedPct / 100,
          ball_speed: ballSpeedPct / 100,
          chaos,
          pulse_speed: pulseSpeedPct / 100,
          flash_rate: flashRatePct / 100,
        }),
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          if (typeof data.entropy === "number") setEntropyUI(data.entropy);
        })
        .catch(() => setEntropyUI(null))
        .finally(() => setLoadingUI(false));
    }, 300);

    return () => {
      clearTimeout(debounceTimeout.current);
      controller.abort();
    };
  }, [cubeSpeedPct, ballSpeedPct, chaos, pulseSpeedPct, flashRatePct]);

  // --- Entropie dynamique (snapshot __oracleSnapshot → backend /dyn_entropy)
  useEffect(() => {
    setLoadingDyn(true);
    let intervalId = null;

    function sendDynEntropySnapshot() {
      const canvas = canvasRef.current;
      if (!canvas || !canvas.__oracleSnapshot) return;

      const snapshot = canvas.__oracleSnapshot();
      fetch("/api/geometry/cubes/dyn_entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(snapshot),
      })
        .then((r) => r.json())
        .then((data) => {
          if (typeof data.entropy === "number") setEntropyDyn(data.entropy);
        })
        .catch(() => setEntropyDyn(null))
        .finally(() => setLoadingDyn(false));
    }

    intervalId = setInterval(sendDynEntropySnapshot, DYN_ENTROPY_INTERVAL);
    return () => { if (intervalId) clearInterval(intervalId); };
  }, []);

  // ⚡ Score total (SANS plafond artificiel)
  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  return (
    <div className="page-container">
      <h1>Cubes Visualizer</h1>

      {/* Trois jauges */}
      <div className="gauges-row">
        <div className="card small">
          <h2>Entropie UI</h2>
          <div className="value ui">
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card small">
          <h2 className="dyn-title">Entropie Dynamique</h2>
          <div className="value dyn">
            {loadingDyn ? "⏳" : entropyDyn !== null ? entropyDyn.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card small">
          <h2 className="total-title">Score Total</h2>
          <div className="value total">{entropyTotal}</div>
        </div>
      </div>

      {/* Contrôles + Canvas */}
      <div className="controls-row">
        {/* Contrôles */}
        <div className="controls-panel">
          <button className="generate-button" onClick={() => setRunning((p) => !p)}>
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            className={`toggle-button ${chaos ? "active-chaos" : "inactive-chaos"}`}
            onClick={() => setChaos((p) => !p)}
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            className={`toggle-button ${glow ? "active-glow" : "inactive-glow"}`}
            onClick={() => setGlow((p) => !p)}
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          <div className="input-group">
            <label>Vitesse cubes : {cubeSpeedPct}%</label>
            <input type="range" min={0} max={100} value={cubeSpeedPct} onChange={(e) => setCubeSpeedPct(Number(e.target.value))} />
          </div>

          <div className="input-group">
            <label>Vitesse billes : {ballSpeedPct}%</label>
            <input type="range" min={0} max={100} value={ballSpeedPct} onChange={(e) => setBallSpeedPct(Number(e.target.value))} />
          </div>

          <div className="input-group">
            <label>Pulsation : {pulseSpeedPct}%</label>
            <input type="range" min={0} max={100} value={pulseSpeedPct} onChange={(e) => setPulseSpeedPct(Number(e.target.value))} />
          </div>

          <div className="input-group">
            <label>Flash : {flashRatePct}%</label>
            <input type="range" min={0} max={100} value={flashRatePct} onChange={(e) => setFlashRatePct(Number(e.target.value))} />
          </div>
        </div>

        {/* Canvas (pur, sans double card) */}
        <Cubes3D
          ref={canvasRef}
          running={running}
          chaos={chaos}
          glow={glow}
          cubeSpeedPct={cubeSpeedPct}
          ballSpeedPct={ballSpeedPct}
          pulseSpeedPct={pulseSpeedPct}
          flashRatePct={flashRatePct}
        />
      </div>
    </div>
  );
}
